import { formatMMDDYYYYToYYYYMMDD } from '@/utils/formatDate';
import type { ImportantDateActionItem, ImportantDatesFormValues } from '../../sections/importantDatesSection/types';

function toApiDate(value: string | null | undefined): string {
  if (!value || typeof value !== 'string') return '';
  return formatMMDDYYYYToYYYYMMDD(value) ?? value;
}

export interface GetImportantDatesPayloadOptions {
  /** Initial important dates when the modal opened; used to detect removed rows (delete actions). */
  initialImportantDates?: { id: number }[];
}

/** Placeholder id for empty row when modal has no saved items; must be treated as create, not update. */
const MOCK_IMPORTANT_DATE_ID = 0;

function isRealImportantDateId(id: number | null | undefined): id is number {
  return id != null && id !== MOCK_IMPORTANT_DATE_ID;
}

export function getImportantDatesPayload(
  values: ImportantDatesFormValues,
  options?: GetImportantDatesPayloadOptions
): ImportantDateActionItem[] {
  const current = values.important_dates ?? [];
  const currentIds = new Set(
    current.map((row) => row.id).filter(isRealImportantDateId)
  );
  const initialIds = new Set(
    (options?.initialImportantDates ?? [])
      .map((d) => d.id)
      .filter(isRealImportantDateId)
  );

  const items: ImportantDateActionItem[] = [];

  // Deletes: real ids that were in initial data but are no longer in the form (user removed the row)
  for (const id of initialIds) {
    if (!currentIds.has(id)) {
      items.push({
        id,
        name: '',
        event_date: '',
        event_frequency_id: null,
        notes: null,
        action: 'delete',
      });
    }
  }

  for (const row of current) {
    const name = row.name ?? '';
    const event_date = toApiDate(row.event_date);
    const event_frequency_id = row.event_frequency_id ?? 0;
    const notes = row.notes ?? null;

    if (isRealImportantDateId(row.id) && initialIds.has(row.id)) {
      items.push({
        id: row.id,
        name,
        event_date,
        event_frequency_id,
        notes,
        action: 'update',
      });
    } else {
      // id is null, undefined, or mock id 0 → create
      items.push({
        name,
        event_date,
        event_frequency_id,
        notes,
        action: 'create',
      });
    }
  }

  return items;
}
